import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/services/auth.service';
import { CoachMessage, CoachProductLine } from './coaching.service';

export interface CoachingMessageEvent {
  thread_id: string;
  assignment_id?: string;
  product_line?: CoachProductLine;
  message: CoachMessage;
}

export interface CoachingNewClientEvent {
  assignment_id: string;
  user_id: string;
  product_line: CoachProductLine;
  thread_id: string | null;
}

export interface CoachingUnknownWhatsAppEvent {
  id: string;
  phone_digits: string;
  display_number: string;
  product_line: CoachProductLine;
  content: string;
  sent_at?: string | null;
  external_id: string;
}

/**
 * Socket.IO client for coaching realtime events.
 * Backend joins every authenticated socket to `user:{id}` and emits
 * `coaching:message` / `coaching:new_client` there.
 */
@Injectable({ providedIn: 'root' })
export class CoachingSocketService {
  private socket: Socket | null = null;
  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  private readonly messageSubject = new Subject<CoachingMessageEvent>();
  private readonly newClientSubject = new Subject<CoachingNewClientEvent>();
  private readonly unknownSubject = new Subject<CoachingUnknownWhatsAppEvent>();

  readonly connected$ = this.connectedSubject.asObservable();
  readonly messages$ = this.messageSubject.asObservable();
  readonly newClients$ = this.newClientSubject.asObservable();
  readonly unknownWhatsApp$ = this.unknownSubject.asObservable();

  constructor(private auth: AuthService) {}

  connect(): void {
    const token = this.auth.getToken();
    if (!token) {
      return;
    }
    if (this.socket?.connected) {
      return;
    }

    this.disconnect();

    this.socket = io(this.socketOrigin(), {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000
    });

    this.socket.on('connect', () => this.connectedSubject.next(true));
    this.socket.on('disconnect', () => this.connectedSubject.next(false));
    this.socket.on('connect_error', () => this.connectedSubject.next(false));

    this.socket.on('coaching:message', (payload: CoachingMessageEvent) => {
      if (payload?.thread_id && payload?.message) {
        this.messageSubject.next(payload);
      }
    });

    this.socket.on('coaching:new_client', (payload: CoachingNewClientEvent) => {
      if (payload?.assignment_id) {
        this.newClientSubject.next(payload);
      }
    });

    this.socket.on('coaching:unknown_whatsapp', (payload: CoachingUnknownWhatsAppEvent) => {
      if (payload?.phone_digits) {
        this.unknownSubject.next(payload);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectedSubject.next(false);
  }

  get isConnected(): boolean {
    return !!this.socket?.connected;
  }

  messagesForThread(threadId: string): Observable<CoachingMessageEvent> {
    return this.messages$.pipe(filter(e => e.thread_id === threadId));
  }

  /** API is `http://host/api` or `http://host/api/v1` — Socket.IO lives on the host origin. */
  private socketOrigin(): string {
    try {
      const url = new URL(environment.api, window.location.origin);
      return url.origin;
    } catch {
      return window.location.origin;
    }
  }
}
